// =====================================================================
// TEST MODE – GPS SIMULATOR SERVICE
// ---------------------------------------------------------------------
// Exposes a drop-in replacement for `navigator.geolocation` that emits
// positions moving along a real Google Directions road route.
//
// The driver page keeps its production watchPosition callback, so every
// simulated fix goes through the exact same path as a real GPS fix:
//   setDriverLocation (marker A) -> socket "driverLocation" ->
//   backend relays "updateDriverLocation" to the ride's passengers ->
//   60s DB sync via save_current_location_of_driver.
//
// Trigger: chat MsgId 15 ("Ride accepted (OTP)") for the active ride.
// Coordinates are generated ONLY here. The passenger app is untouched and
// receives them through the normal backend relay.
// =====================================================================

import * as testModeConfig from './testModeConfig';
import { getRoutePath } from '../utils/routeProgress';
import { getDriverRideDataFromStorage, setDriverRideDataInStorage } from '../utils/localStorage';

const testModeLogPrefix = '[testMode GPS]';
const testModeWatchIdPrefix = 'testMode-gps-';

let testModeWatchSequence = 0;
let testModeActiveSimulation = null;
let testModeLastStatus = { state: 'idle', reason: 'not started yet' };

// ---------------------------------------------------------------------
// Logging (driver browser console). Filter the console by "testMode".
// ---------------------------------------------------------------------

const testModeLogStyle = 'background:#6b21a8;color:#fff;padding:1px 6px;border-radius:3px;font-weight:bold';
export function testModeLog(testModeMessage, ...testModeDetails) {
    if (process.env.NODE_ENV === 'production') return;
    console.log(`%c${testModeLogPrefix}`, testModeLogStyle, testModeMessage, ...testModeDetails);
}
export function testModeWarn(testModeMessage, ...testModeDetails) {
    if (process.env.NODE_ENV === 'production') return;
    console.warn(`%c${testModeLogPrefix}`, testModeLogStyle, testModeMessage, ...testModeDetails);
}
const setTestModeStatus = (testModeState, testModeReason) => {
    testModeLastStatus = { state: testModeState, reason: testModeReason, at: new Date().toLocaleTimeString() };
};

// ---------------------------------------------------------------------
// Gating
// ---------------------------------------------------------------------

export const isTestModeProductionBuild = () => process.env.NODE_ENV === 'production';

// testMode switch on and not a production build.
export const isTestModeAvailable = () => testModeConfig.testMode === true && !isTestModeProductionBuild();

// Reads the message id whatever the API casing is (MsgId, msgId, MsgID, msgid...).
export function getTestModeChatMsgId(testModeChat) {
    if (!testModeChat || typeof testModeChat !== 'object') return null;
    const testModeKey = Object.keys(testModeChat).find(testModeName => testModeName.toLowerCase() === 'msgid');
    return testModeKey ? Number(testModeChat[testModeKey]) : null;
}

// True when a chat list contains the trigger message (MsgId 15).
export const hasTestModeTriggerMessage = testModeChats =>
    Array.isArray(testModeChats) && testModeChats.some(testModeChat =>
        getTestModeChatMsgId(testModeChat) === Number(testModeConfig.testModeTriggerMsgId));

// Logs what a chat list contains, so a missing MsgId 15 is easy to spot.
export function logTestModeChatCheck(testModeSource, testModeChats) {
    if (!isTestModeAvailable()) return;
    const testModeIds = Array.isArray(testModeChats) ? testModeChats.map(getTestModeChatMsgId) : [];
    const testModeFound = hasTestModeTriggerMessage(testModeChats);
    testModeLog(`chat check (${testModeSource}): MsgIds = [${testModeIds.join(', ')}] -> ` +
        (testModeFound ? `MsgId ${testModeConfig.testModeTriggerMsgId} FOUND` : `no MsgId ${testModeConfig.testModeTriggerMsgId}`));
    if (Array.isArray(testModeChats) && testModeChats.length && testModeIds.every(testModeId => testModeId == null)) {
        testModeWarn('chat rows have no MsgId field. First row keys:', Object.keys(testModeChats[0] || {}));
    }
}

// Simulation is enabled only for the ride that received MsgId 15.
export function isTestModeGpsSimulationEnabled(testModeRideSession = getDriverRideDataFromStorage()) {
    return isTestModeAvailable() &&
        Number(testModeRideSession?.DriveNo) > 0 &&
        !testModeRideSession.trackingComplete &&
        String(testModeRideSession.testModeRideAcceptedDriveNo) === String(testModeRideSession.DriveNo);
}

/**
 * Records that the active ride received MsgId 15 (stored in the existing
 * driver_ride storage, so it is cleared with the ride). Returns true when
 * the simulator should now drive this ride.
 */
export function markTestModeRideAccepted(testModePickup, testModeSource = 'unknown') {
    if (!isTestModeAvailable()) return false;
    const testModeRideSession = getDriverRideDataFromStorage();
    if (!(Number(testModeRideSession.DriveNo) > 0)) {
        testModeWarn(`MsgId ${testModeConfig.testModeTriggerMsgId} via ${testModeSource} ignored: no active DriveNo in driver_ride storage`, testModeRideSession);
        setTestModeStatus('not started', 'no active DriveNo');
        return false;
    }
    if (testModeRideSession.trackingComplete) {
        testModeWarn(`MsgId ${testModeConfig.testModeTriggerMsgId} via ${testModeSource} ignored: ride ${testModeRideSession.DriveNo} tracking already complete`);
        setTestModeStatus('not started', 'ride tracking complete');
        return false;
    }
    const testModePickupPoint = testModeToPoint(testModePickup) || testModeRideSession.testModePickup || null;
    if (String(testModeRideSession.testModeRideAcceptedDriveNo) !== String(testModeRideSession.DriveNo) ||
        JSON.stringify(testModePickupPoint) !== JSON.stringify(testModeRideSession.testModePickup ?? null)) {
        setDriverRideDataInStorage({
            testModeRideAcceptedDriveNo: testModeRideSession.DriveNo,
            testModePickup: testModePickupPoint,
        });
        testModeLog(`MsgId ${testModeConfig.testModeTriggerMsgId} received via ${testModeSource} for DriveNo ${testModeRideSession.DriveNo}. ` +
            `Pickup: ${testModePickupPoint ? `${testModePickupPoint.lat}, ${testModePickupPoint.lng}` : 'unknown (route start will be used)'}`);
    }
    return true;
}

function testModeToPoint(testModeLocation) {
    const lat = Number(testModeLocation?.lat);
    const lng = Number(testModeLocation?.lng);
    return testModeLocation?.lat != null && testModeLocation?.lng != null &&
        Number.isFinite(lat) && Number.isFinite(lng) ? { lat, lng } : null;
}

export const isTestModeWatchId = testModeWatchId =>
    typeof testModeWatchId === 'string' && testModeWatchId.startsWith(testModeWatchIdPrefix);

// ---------------------------------------------------------------------
// Geometry helpers (pure)
// ---------------------------------------------------------------------

export function testModeDistanceMeters(testModeFrom, testModeTo) {
    const testModeRad = Math.PI / 180;
    const testModeDLat = (testModeTo.lat - testModeFrom.lat) * testModeRad;
    const testModeDLng = (testModeTo.lng - testModeFrom.lng) * testModeRad;
    const testModeA = Math.sin(testModeDLat / 2) ** 2 +
        Math.cos(testModeFrom.lat * testModeRad) * Math.cos(testModeTo.lat * testModeRad) *
        Math.sin(testModeDLng / 2) ** 2;
    return 6371000 * 2 * Math.atan2(Math.sqrt(testModeA), Math.sqrt(1 - testModeA));
}

export function testModeHeadingDegrees(testModeFrom, testModeTo) {
    const testModeRad = Math.PI / 180;
    const testModeY = Math.sin((testModeTo.lng - testModeFrom.lng) * testModeRad) * Math.cos(testModeTo.lat * testModeRad);
    const testModeX = Math.cos(testModeFrom.lat * testModeRad) * Math.sin(testModeTo.lat * testModeRad) -
        Math.sin(testModeFrom.lat * testModeRad) * Math.cos(testModeTo.lat * testModeRad) *
        Math.cos((testModeTo.lng - testModeFrom.lng) * testModeRad);
    return (Math.atan2(testModeY, testModeX) / testModeRad + 360) % 360;
}

// Drops invalid / duplicate vertices and precomputes cumulative distance.
export function buildTestModeRouteCoordinates(testModeRawPath) {
    const testModeRouteCoordinates = [];
    (testModeRawPath || []).forEach(testModePoint => {
        const lat = Number(typeof testModePoint?.lat === 'function' ? testModePoint.lat() : testModePoint?.lat);
        const lng = Number(typeof testModePoint?.lng === 'function' ? testModePoint.lng() : testModePoint?.lng);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;
        const testModePrevious = testModeRouteCoordinates[testModeRouteCoordinates.length - 1];
        if (testModePrevious && testModePrevious.lat === lat && testModePrevious.lng === lng) return;
        testModeRouteCoordinates.push({
            lat, lng,
            distance: testModePrevious ? testModePrevious.distance + testModeDistanceMeters(testModePrevious, { lat, lng }) : 0,
        });
    });
    return testModeRouteCoordinates;
}

// Interpolated point at `testModeDistance` metres along the route.
// `testModeHint` is the last segment index, so the search is monotonic O(1).
export function getTestModeLocationAtDistance(testModeRouteCoordinates, testModeDistance, testModeHint = 0) {
    const testModeLast = testModeRouteCoordinates.length - 1;
    if (testModeLast < 1 || testModeDistance >= testModeRouteCoordinates[testModeLast].distance) {
        const testModeEnd = testModeRouteCoordinates[testModeLast];
        const testModeBeforeEnd = testModeRouteCoordinates[Math.max(0, testModeLast - 1)];
        return {
            lat: testModeEnd.lat, lng: testModeEnd.lng, segmentIndex: Math.max(0, testModeLast - 1),
            heading: testModeHeadingDegrees(testModeBeforeEnd, testModeEnd), arrived: true,
        };
    }
    let testModeIndex = Math.max(0, Math.min(testModeHint, testModeLast - 1));
    while (testModeIndex < testModeLast - 1 && testModeRouteCoordinates[testModeIndex + 1].distance <= testModeDistance) {
        testModeIndex++;
    }
    const testModeStart = testModeRouteCoordinates[testModeIndex];
    const testModeEnd = testModeRouteCoordinates[testModeIndex + 1];
    const testModeSegmentLength = testModeEnd.distance - testModeStart.distance;
    const testModeFraction = testModeSegmentLength > 0
        ? Math.max(0, Math.min(1, (testModeDistance - testModeStart.distance) / testModeSegmentLength)) : 0;
    return {
        lat: testModeStart.lat + (testModeEnd.lat - testModeStart.lat) * testModeFraction,
        lng: testModeStart.lng + (testModeEnd.lng - testModeStart.lng) * testModeFraction,
        segmentIndex: testModeIndex,
        heading: testModeHeadingDegrees(testModeStart, testModeEnd),
        arrived: false,
    };
}

// Start the drive where the OTP happened: project the pickup onto the route
// and drop everything before it. Far-away pickups leave the route unchanged.
export function trimTestModeRouteFromPoint(testModeRouteCoordinates, testModeStartPoint, testModeMaxMeters = 3000) {
    const testModeStart = testModeToPoint(testModeStartPoint);
    if (!testModeStart || testModeRouteCoordinates.length < 2) return testModeRouteCoordinates;
    const testModeLngScale = Math.cos(testModeStart.lat * Math.PI / 180);
    let testModeBest = { distance: Infinity, index: 0, point: testModeRouteCoordinates[0] };
    for (let i = 0; i < testModeRouteCoordinates.length - 1; i++) {
        const testModeA = testModeRouteCoordinates[i];
        const testModeB = testModeRouteCoordinates[i + 1];
        const testModeDx = (testModeB.lng - testModeA.lng) * testModeLngScale;
        const testModeDy = testModeB.lat - testModeA.lat;
        const testModeLen = testModeDx * testModeDx + testModeDy * testModeDy;
        const testModeT = testModeLen ? Math.max(0, Math.min(1,
            ((testModeStart.lng - testModeA.lng) * testModeLngScale * testModeDx + (testModeStart.lat - testModeA.lat) * testModeDy) / testModeLen)) : 0;
        const testModePoint = {
            lat: testModeA.lat + (testModeB.lat - testModeA.lat) * testModeT,
            lng: testModeA.lng + (testModeB.lng - testModeA.lng) * testModeT,
        };
        const testModeDistance = testModeDistanceMeters(testModeStart, testModePoint);
        if (testModeDistance < testModeBest.distance - 0.1) testModeBest = { distance: testModeDistance, index: i, point: testModePoint };
    }
    if (testModeBest.distance > testModeMaxMeters) return testModeRouteCoordinates;
    return buildTestModeRouteCoordinates([testModeBest.point, ...testModeRouteCoordinates.slice(testModeBest.index + 1)]);
}

// Gentle acceleration over the first 60 m and braking over the last 60 m
// so the vehicle does not jump to full speed or stop instantly.
export function getTestModeSpeedMps(testModeTravelled, testModeTotal, testModeSpeedKmph) {
    const testModeCruise = Math.max(1, Number(testModeSpeedKmph) || 30) / 3.6;
    const testModeRamp = 60;
    const testModeFactor = Math.min(1,
        Math.max(0.25, testModeTravelled / testModeRamp),
        Math.max(0.25, (testModeTotal - testModeTravelled) / testModeRamp));
    return testModeCruise * testModeFactor;
}

// ---------------------------------------------------------------------
// Route resolution
// ---------------------------------------------------------------------

const testModeToLatLngOrAddress = testModeLocation =>
    testModeLocation && typeof testModeLocation === 'object'
        ? new window.google.maps.LatLng(Number(testModeLocation.lat), Number(testModeLocation.lng))
        : testModeLocation;

async function fetchTestModeConfiguredRoute() {
    const testModeDirectionsService = new window.google.maps.DirectionsService();
    const testModeResult = await testModeDirectionsService.route({
        origin: testModeToLatLngOrAddress(testModeConfig.testModeStartLocation),
        destination: testModeToLatLngOrAddress(testModeConfig.testModeEndLocation),
        travelMode: window.google.maps.TravelMode.DRIVING,
    });
    return testModeResult?.routes?.[0] || null;
}

const testModeDelay = testModeMs => new Promise(testModeResolve => setTimeout(testModeResolve, testModeMs));

async function resolveTestModeRoute(testModeGetRideRoute, testModeIsCancelled) {
    if (testModeConfig.testModeStartLocation && testModeConfig.testModeEndLocation) {
        testModeLog('requesting custom route from Google Directions', testModeConfig.testModeStartLocation, '->', testModeConfig.testModeEndLocation);
        return fetchTestModeConfiguredRoute();
    }
    let testModeWaitLogged = false;
    // Follow the ride's selected route. On a restored session the route is
    // still being fetched when tracking starts, so wait for it.
    const testModeDeadline = Date.now() + (Number(testModeConfig.testModeRouteWaitTimeoutMs) || 60000);
    while (!testModeIsCancelled()) {
        const testModeRideRoute = testModeGetRideRoute?.();
        if (getRoutePath(testModeRideRoute).length > 1) {
            testModeLog('using the ride route shown on the driver map');
            return testModeRideRoute;
        }
        if (!testModeWaitLogged) {
            testModeLog('waiting for the ride route to load on the driver map...');
            setTestModeStatus('waiting', 'waiting for ride route');
            testModeWaitLogged = true;
        }
        if (Date.now() >= testModeDeadline) return null;
        await testModeDelay(500);
    }
    return null;
}

// ---------------------------------------------------------------------
// Simulation lifecycle
// ---------------------------------------------------------------------

export function stopTestModeGpsSimulation(testModeWatchId, testModeReason = 'ride ended / tracking cleared') {
    if (!testModeActiveSimulation) return;
    if (testModeWatchId != null && testModeWatchId !== testModeActiveSimulation.id) return;
    testModeActiveSimulation.cancelled = true;
    if (testModeActiveSimulation.timer) clearInterval(testModeActiveSimulation.timer);
    testModeLog(`STOPPED ${testModeActiveSimulation.id} (${testModeReason}) after ${testModeActiveSimulation.ticks || 0} updates`);
    setTestModeStatus('stopped', testModeReason);
    testModeActiveSimulation = null;
}

// Console helper: type testModeGpsStatus() in the driver browser console.
export function getTestModeGpsStatus() {
    const testModeRideSession = getDriverRideDataFromStorage();
    return {
        testMode: testModeConfig.testMode,
        buildMode: process.env.NODE_ENV,
        available: isTestModeAvailable(),
        triggerMsgId: testModeConfig.testModeTriggerMsgId,
        driveNo: testModeRideSession.DriveNo ?? null,
        msgId15ReceivedForDriveNo: testModeRideSession.testModeRideAcceptedDriveNo ?? null,
        enabledForThisRide: isTestModeGpsSimulationEnabled(testModeRideSession),
        trackingComplete: !!testModeRideSession.trackingComplete,
        pickup: testModeRideSession.testModePickup ?? null,
        running: !!testModeActiveSimulation,
        simulationId: testModeActiveSimulation?.id ?? null,
        updatesSent: testModeActiveSimulation?.ticks ?? 0,
        currentLocation: testModeActiveSimulation?.currentLocation ?? null,
        progress: testModeActiveSimulation?.progress ?? null,
        lastStatus: testModeLastStatus,
    };
}

export const getTestModeCurrentLocation = () => testModeActiveSimulation?.currentLocation || null;

/**
 * Same contract as navigator.geolocation.watchPosition: returns a watch id
 * synchronously and calls `testModeOnPosition` with GeolocationPosition-like
 * objects. Only one simulation runs at a time.
 */
export function startTestModeGpsSimulation(testModeOnPosition, testModeOnError, testModeGetRideRoute, testModeGetStartPoint) {
    stopTestModeGpsSimulation();
    const testModeSimulation = {
        id: `${testModeWatchIdPrefix}${++testModeWatchSequence}`,
        cancelled: false,
        timer: null,
        currentLocation: null,
        ticks: 0,
        progress: null,
    };
    setTestModeStatus('starting', 'resolving route');
    testModeLog(`starting simulator ${testModeSimulation.id}...`);
    testModeActiveSimulation = testModeSimulation;
    const testModeIsCancelled = () => testModeSimulation.cancelled;

    const testModeFail = testModeMessage => {
        console.error(`${testModeLogPrefix} FAILED: ${testModeMessage}`);
        if (testModeActiveSimulation === testModeSimulation) stopTestModeGpsSimulation(testModeSimulation.id, `failed: ${testModeMessage}`);
        testModeOnError?.({ code: 2, message: `${testModeLogPrefix} ${testModeMessage}` });
    };

    resolveTestModeRoute(testModeGetRideRoute, testModeIsCancelled).then(testModeRoute => {
        if (testModeIsCancelled()) return;
        const testModeUsesRideRoute = !(testModeConfig.testModeStartLocation && testModeConfig.testModeEndLocation);
        const testModeRouteCoordinates = trimTestModeRouteFromPoint(
            buildTestModeRouteCoordinates(getRoutePath(testModeRoute)),
            testModeUsesRideRoute ? testModeGetStartPoint?.() : null,
            Number(testModeConfig.testModePickupSnapMeters) || 3000);
        if (testModeRouteCoordinates.length < 2) {
            testModeFail('no usable route – check testModeStartLocation/testModeEndLocation or the ride route');
            return;
        }

        const testModeIntervalMs = Math.max(250, Number(testModeConfig.testModeUpdateIntervalMs) || 2000);
        const testModeTotal = testModeRouteCoordinates[testModeRouteCoordinates.length - 1].distance;
        let testModeTravelled = 0;
        let testModeSegmentHint = 0;

        const testModeLocationUpdateHandler = () => {
            if (testModeIsCancelled()) return;
            const testModeSpeed = getTestModeSpeedMps(testModeTravelled, testModeTotal, testModeConfig.testModeSpeedKmph);
            const testModePoint = getTestModeLocationAtDistance(testModeRouteCoordinates, testModeTravelled, testModeSegmentHint);
            testModeSegmentHint = testModePoint.segmentIndex;
            testModeSimulation.currentLocation = { lat: testModePoint.lat, lng: testModePoint.lng };
            testModeSimulation.ticks++;
            testModeSimulation.progress = `${(testModeTravelled / 1000).toFixed(2)} / ${(testModeTotal / 1000).toFixed(2)} km`;
            testModeLog(`#${testModeSimulation.ticks} ${testModePoint.lat.toFixed(6)}, ${testModePoint.lng.toFixed(6)} | ` +
                `${testModeSimulation.progress} (${Math.round(testModeTravelled / testModeTotal * 100)}%) | ` +
                `${(testModePoint.arrived ? 0 : testModeSpeed * 3.6).toFixed(0)} km/h -> sent to driver tracking (socket driverLocation)`);

            testModeOnPosition({
                coords: {
                    latitude: testModePoint.lat,
                    longitude: testModePoint.lng,
                    accuracy: 5,
                    altitude: null,
                    altitudeAccuracy: null,
                    heading: testModePoint.heading,
                    speed: testModePoint.arrived ? 0 : testModeSpeed,
                },
                timestamp: Date.now(),
                testMode: true,
            });

            if (testModePoint.arrived) {
                // Destination reached: stop producing fixes. The vehicle stays
                // parked at the last point, exactly like a stationary real GPS.
                testModeLog(`ARRIVED at destination after ${testModeSimulation.ticks} updates. Simulator idle (vehicle parked).`);
                setTestModeStatus('arrived', 'destination reached');
                clearInterval(testModeSimulation.timer);
                testModeSimulation.timer = null;
                return;
            }
            testModeTravelled = Math.min(testModeTotal, testModeTravelled + testModeSpeed * testModeIntervalMs / 1000);
        };

        testModeLog(`STARTED ${testModeSimulation.id}: ${(testModeTotal / 1000).toFixed(2)} km ` +
            `from ${testModeRouteCoordinates[0].lat.toFixed(6)}, ${testModeRouteCoordinates[0].lng.toFixed(6)} ` +
            `@ ${testModeConfig.testModeSpeedKmph} km/h, update every ${testModeIntervalMs} ms`);
        setTestModeStatus('running', 'driving simulated route');
        // Start the timer first so the handler can clear it on arrival,
        // then emit the first fix immediately, like a GPS lock.
        testModeSimulation.timer = setInterval(testModeLocationUpdateHandler, testModeIntervalMs);
        testModeLocationUpdateHandler();
    }).catch(testModeError => {
        if (!testModeIsCancelled()) testModeFail(`route request failed: ${testModeError?.message || testModeError}`);
    });

    return testModeSimulation.id;
}

function logTestModeWatchDecision() {
    const testModeRideSession = getDriverRideDataFromStorage();
    if (isTestModeGpsSimulationEnabled(testModeRideSession)) {
        testModeLog(`GPS source = SIMULATOR (DriveNo ${testModeRideSession.DriveNo} has MsgId ${testModeConfig.testModeTriggerMsgId})`);
    } else {
        const testModeReason = !(Number(testModeRideSession.DriveNo) > 0) ? 'no active DriveNo'
            : testModeRideSession.trackingComplete ? 'ride tracking complete'
                : `waiting for MsgId ${testModeConfig.testModeTriggerMsgId} (OTP / Ride accepted) for DriveNo ${testModeRideSession.DriveNo}`;
        testModeLog(`GPS source = REAL device GPS (${testModeReason})`);
        setTestModeStatus('real GPS', testModeReason);
    }
    return true;
}

// One-time banner when the app loads (development builds only).
if (process.env.NODE_ENV !== 'production' && process.env.NODE_ENV !== 'test') {
    if (testModeConfig.testMode === true) {
        testModeLog(`testMode is ON. Simulator starts when the ride chat gets MsgId ${testModeConfig.testModeTriggerMsgId} ` +
            `("Ride accepted (OTP)"). Type testModeGpsStatus() in this console any time.`);
    } else {
        testModeLog('testMode is OFF (set testMode = true in src/testMode/testModeConfig.js to enable the GPS simulator)');
    }
    if (typeof window !== 'undefined') window.testModeGpsStatus = () => {
        const testModeStatus = getTestModeGpsStatus();
        console.table(testModeStatus);
        return testModeStatus;
    };
}

/**
 * Geolocation source for the driver page, same API as navigator.geolocation.
 * The choice is made on every watchPosition call:
 * - testMode off / no MsgId 15 for this ride / production build -> real GPS.
 * - testMode on and the active ride received MsgId 15 -> simulator.
 */
export function getTestModeGeolocation(testModeGetRideRoute, testModeGetStartPoint) {
    const testModeRealGeolocation = typeof navigator !== 'undefined' ? navigator.geolocation : undefined;
    if (!isTestModeAvailable()) return testModeRealGeolocation;
    return {
        testMode: true,
        watchPosition: (testModeOnPosition, testModeOnError, testModeOptions) =>
            logTestModeWatchDecision() && isTestModeGpsSimulationEnabled()
                ? startTestModeGpsSimulation(testModeOnPosition, testModeOnError, testModeGetRideRoute,
                    testModeGetStartPoint || (() => getDriverRideDataFromStorage().testModePickup))
                // Real GPS until MsgId 15; drop any late real fix once the simulator owns tracking.
                : navigator.geolocation?.watchPosition(
                    testModePosition => { if (!testModeActiveSimulation) testModeOnPosition(testModePosition); },
                    testModeOnError, testModeOptions),
        clearWatch: testModeWatchId => {
            if (isTestModeWatchId(testModeWatchId)) stopTestModeGpsSimulation(testModeWatchId);
            else navigator.geolocation?.clearWatch(testModeWatchId);
        },
        getCurrentPosition: (...testModeArgs) => navigator.geolocation?.getCurrentPosition(...testModeArgs),
    };
}
